import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { t } from '../i18n';
import { api } from '../lib/api';
import { Button, Callout, Checkbox, Ltr, Select, useToast } from '../components/ui';
import { IconUpload } from '../components/icons';

type Field = 'businessName' | 'companyNumber' | 'contactName' | 'phone' | 'email' | 'website' | 'address' | 'city' | 'street' | 'status' | 'paymentTerms' | 'notes';
type Row = {
  rowNumber: number;
  businessName: string;
  companyNumber: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  status: 'active' | 'inactive' | 'prospect';
  paymentTerms: 'IMMEDIATE' | 'EOM_FIXED_DAY' | 'EOM_30' | 'EOM_45' | 'EOM_60';
  notes: string | null;
  address: { street: string | null; buildingNumber: string | null; city: string | null } | null;
  errors: string[];
  warnings: string[];
  duplicate: { customerId: number | null; name: string; reason: string } | null;
  action: 'create' | 'skip';
};
type Preview = { headers: string[]; mapping: Record<string, Field | null>; rows: Row[]; sample: Array<Record<string, string>>; needsMapping: boolean; fields: Field[] };

const FIELD_LABELS: Record<Field, string> = {
  businessName: 'שם העסק',
  companyNumber: 'ח.פ / ע.מ',
  contactName: 'איש קשר',
  phone: 'טלפון',
  email: 'אימייל',
  website: 'אתר',
  address: 'כתובת (טקסט חופשי)',
  city: 'עיר',
  street: 'רחוב ומספר',
  status: 'סטטוס',
  paymentTerms: 'תנאי תשלום',
  notes: 'הערות',
};

export function ImportCustomersPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ created: number; skipped: number; attachedDocuments?: number; skippedRows: Array<{ rowNumber: number; businessName: string; reason: string }> } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const runPreview = async (f: File, mapping?: Record<string, Field | null>) => {
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      if (mapping) form.append('mapping', JSON.stringify(mapping));
      form.append('file', f);
      const p = await api<Preview>('/api/import/customers/preview', { form });
      setPreview(p);
      setSelected(new Set(p.rows.filter((r) => r.action === 'create').map((r) => r.rowNumber)));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => selected.has(r.rowNumber)).map(({ errors: _e, warnings: _w, duplicate: _d, action: _a, ...r }) => r);
      const r = await api<typeof result>('/api/import/customers/commit', { body: { rows } });
      setResult(r);
      for (const k of ['customers', 'customer-options', 'dashboard']) qc.invalidateQueries({ queryKey: [k] });
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };

  const creatable = preview?.rows.filter((r) => r.action === 'create') ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <div className="crumbs"><Link to="/customers">{t.customers.title}</Link></div>
          <h1>ייבוא לקוחות מקובץ</h1>
          <div className="sub">Excel (xlsx) או CSV. שורה ראשונה עם כותרות. לקוחות קיימים לא ייובאו פעמיים.</div>
        </div>
      </div>

      {result ? (
        <div className="panel panel-body stack">
          <Callout tone="ok" title={`יובאו ${result.created} לקוחות`}>
            {result.skipped ? `${result.skipped} שורות דולגו.` : 'כל השורות שנבחרו יובאו.'}
            {result.attachedDocuments ? ` ${result.attachedDocuments} מסמכים שהמתינו לבדיקה שויכו אוטומטית ללקוחות החדשים.` : ''}
          </Callout>
          {result.skippedRows.length ? (
            <ul className="small muted" style={{ margin: 0 }}>
              {result.skippedRows.map((r) => (
                <li key={r.rowNumber}>שורה {r.rowNumber} · {r.businessName} — {r.reason}</li>
              ))}
            </ul>
          ) : null}
          <div className="row">
            <Button variant="primary" onClick={() => navigate('/customers')}>ללקוחות</Button>
            <Link to="/automation" className="btn">ייבוא היסטוריית מסמכים מהמייל</Link>
          </div>
        </div>
      ) : (
        <div className="stack">
          <div className="panel panel-body">
            <div className="dropzone" onClick={() => input.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}>
              {busy && !preview ? (
                <div className="row" style={{ justifyContent: 'center' }}><span className="spinner" /> קורא את הקובץ…</div>
              ) : (
                <>
                  <IconUpload size={22} />
                  <div className="strong" style={{ marginTop: 8 }}>{file ? file.name : 'בחירת קובץ Excel או CSV'}</div>
                  <div className="small muted">{file ? 'לחצו להחלפת קובץ' : 'העמודות יזוהו אוטומטית — ניתן לתקן את ההתאמה'}</div>
                </>
              )}
            </div>
            <input
              ref={input}
              type="file"
              hidden
              accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setFile(f);
                  void runPreview(f);
                }
                e.target.value = '';
              }}
            />
          </div>

          {preview ? (
            <>
              <section className="panel">
                <div className="panel-head"><h2>התאמת עמודות</h2></div>
                <div className="panel-body">
                  <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>
                    {preview.headers.map((h) => (
                      <label key={h} className="field">
                        <span className="field-label ellipsis" title={h}>{h}</span>
                        <Select
                          value={preview.mapping[h] ?? ''}
                          onChange={(e) => {
                            const next = { ...preview.mapping };
                            const val = (e.target.value || null) as Field | null;
                            // One column per field.
                            if (val) for (const k of Object.keys(next)) if (next[k] === val) next[k] = null;
                            next[h] = val;
                            if (file) void runPreview(file, next);
                          }}
                        >
                          <option value="">לא לייבא</option>
                          {preview.fields.map((f) => (
                            <option key={f} value={f}>{FIELD_LABELS[f]}</option>
                          ))}
                        </Select>
                      </label>
                    ))}
                  </div>
                  {preview.needsMapping ? <Callout tone="warn">יש לבחור איזו עמודה מכילה את שם העסק.</Callout> : null}
                </div>
              </section>

              {!preview.needsMapping ? (
                <section className="panel">
                  <div className="panel-head row wrap">
                    <h2>{preview.rows.length} שורות</h2>
                    <span className="small muted">
                      {creatable.length} ייובאו · {preview.rows.length - creatable.length} ידולגו (שגיאות או כפילויות)
                    </span>
                    <span className="spacer" />
                    <Button variant="primary" onClick={commit} loading={busy} disabled={selected.size === 0}>
                      ייבוא {selected.size} לקוחות
                    </Button>
                  </div>
                  <div className="table-wrap" style={{ maxHeight: 560, overflowY: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}>
                            <Checkbox
                              checked={selected.size > 0 && selected.size === creatable.length}
                              onChange={(v) => setSelected(v ? new Set(creatable.map((r) => r.rowNumber)) : new Set())}
                            >
                              <span className="sr-only">בחירת הכל</span>
                            </Checkbox>
                          </th>
                          <th>שורה</th>
                          <th>שם העסק</th>
                          <th>ח.פ</th>
                          <th className="hide-sm">איש קשר</th>
                          <th className="hide-sm">כתובת</th>
                          <th>סטטוס</th>
                          <th>הערות ייבוא</th>
                        </tr>
                      </thead>
                      <tbody>
                        {preview.rows.map((r) => (
                          <tr key={r.rowNumber} style={r.action === 'skip' ? { background: 'var(--surface-2)' } : undefined}>
                            <td>
                              <Checkbox
                                checked={selected.has(r.rowNumber)}
                                disabled={r.action === 'skip'}
                                onChange={(v) => {
                                  const n = new Set(selected);
                                  if (v) n.add(r.rowNumber);
                                  else n.delete(r.rowNumber);
                                  setSelected(n);
                                }}
                              >
                                <span className="sr-only">בחירה</span>
                              </Checkbox>
                            </td>
                            <td className="muted tabular">{r.rowNumber}</td>
                            <td className="primary">{r.businessName || <span className="faint">—</span>}</td>
                            <td className="tabular">{r.companyNumber ? <Ltr>{r.companyNumber}</Ltr> : <span className="faint">—</span>}</td>
                            <td className="hide-sm">
                              {r.contactName ?? <span className="faint">—</span>}
                              {r.phone ? <span className="sub"><Ltr>{r.phone}</Ltr></span> : null}
                            </td>
                            <td className="hide-sm small">{r.address ? [r.address.street, r.address.buildingNumber, r.address.city].filter(Boolean).join(' ') : <span className="faint">—</span>}</td>
                            <td>{t.enums.customerStatus[r.status]}</td>
                            <td className="small" style={{ maxWidth: 300 }}>
                              {r.errors.map((e) => <div key={e} style={{ color: 'var(--bad)' }}>{e}</div>)}
                              {r.duplicate ? (
                                <div style={{ color: 'var(--warn)' }}>
                                  {r.duplicate.reason}: {r.duplicate.customerId ? <Link to={`/customers/${r.duplicate.customerId}`}>{r.duplicate.name}</Link> : r.duplicate.name}
                                </div>
                              ) : null}
                              {r.warnings.map((w) => <div key={w} className="muted">{w}</div>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </>
  );
}
